<template>
<el-form ref="form" :model="condition" size="small" class="pmf-search-term-form">
  <div class="base-term">
    <el-form-item prop="beginTime" label="时间范围" :rules="[{
      validator: beginTimeValidator
    }]">
      <el-date-picker
        v-model="condition.beginTime"
        type="date"
        format="yyyy年MM月dd日"
        value-format="yyyy/MM/dd 00:00:00"
        :clearable="false"
        :editable="false"
        :picker-options="startDatePickerOptions"
        style="width:100%;"
      />
    </el-form-item>

    <el-form-item prop="endTime" label="结束时间" :rules="[{
      validator: endTimeValidator
    }]">
      <el-date-picker
        v-model="condition.endTime"
        type="date"
        format="yyyy年MM月dd日"
        value-format="yyyy/MM/dd 23:59:59"
        :clearable="false"
        :editable="false"
        :picker-options="endDatePickerOptions"
        style="width:100%;"
      />
    </el-form-item>

    <el-form-item prop="auditType" label="审核类型">
      <el-radio-group v-model="condition.auditType">
        <el-radio-button
          v-for="auditType in auditTypes"
          :key="auditType.value"
          :label="auditType.value"
        >{{auditType.label}}</el-radio-button>
      </el-radio-group>
    </el-form-item>

    <el-form-item prop="dataType" label="数据类型">
      <el-radio-group v-model="condition.dataType" style="width:100%;">
        <el-radio-button
          v-for="dataType in dataTypes"
          :key="dataType.value"
          :label="dataType.value"
        >{{dataType.label}}</el-radio-button>
      </el-radio-group>
    </el-form-item>

    <el-form-item prop="runCount" label="运行次数" :rules="[{
      required: true,
      message: '请输入运行次数'
    }]">
      <el-input-number v-model="condition.runCount" :min="1" :max="30" style="width:100%;" />
    </el-form-item>

    <el-form-item prop="sourceCount" label="源数量" :rules="[{
      required: true,
      message: '请输入源数量'
    }]">
      <el-input-number v-model="condition.sourceCount" :min="1" :max="10" style="width:100%;" />
    </el-form-item>
  </div>

  <div class="select-areas">
    <el-form-item prop="siteId" label="站点选择" class="select-area-form-item" :rules="[{
      required: true,
      message: '请选择站点'
    }]">
      <div class="tree-container border">
        <ul class="site-select-area">
          <li
            v-for="site in sites"
            :key="site.id"
            :class="{
              selected: site.id === condition.siteId
            }"
            @click="onSiteItemClick(site)"
          >{{site.name}}</li>
        </ul>
      </div>
    </el-form-item>
  </div>

  <div class="select-areas">
    <el-form-item prop="factorIds" label="因子选择" class="select-area-form-item" :rules="[{
      type: 'array',
      required: true,
      message: '请至少选择一个因子'
    }]">
      <div class="factor-select-area-wrapper">
        <div>
          <select-ctrl-group
            :ids="condition.factorIds"
            :options="currentSiteFactors"
            @change="onFactorSelectChange"
          />
        </div>

        <div class="divider"></div>

        <ul class="factor-group-select-list">
          <li
            v-for="formatFactorGroup in formatFactorGroups"
            :key="formatFactorGroup.id"
          >
            <div class="factor-group">
              <el-checkbox
                v-model="factorGroupSelectMap[formatFactorGroup.id]"
                :indeterminate="selectedCount[formatFactorGroup.id] > 0 && selectedCount[formatFactorGroup.id] < formatFactorGroup.children.length"
                @change="onFactorGroupCheckChange(formatFactorGroup, ...arguments)"
              >{{formatFactorGroup.name}}</el-checkbox>
            </div>
            <div class="factor-select-list">
              <el-checkbox
                v-for="factor in formatFactorGroup.children"
                :key="factor.id"
                v-model="condition.factorIds"
                :label="factor.id"
                @change="onFactorSelectChange"
              >{{factor.name}}</el-checkbox>
            </div>
          </li>
        </ul>
      </div>
    </el-form-item>
  </div>
</el-form>
</template>

<script>
import BaseStep from '../BaseStep'

const baseRunKeyMap = {
  id: true,
  runCount: true,
  sourceCount: true
}

export default {
  name: 'TermInputStep',
  label: '选择条件',
  stepDataKey: 'task',
  extends: BaseStep,
  data: () => ({
    factorGroupSelectMap: {}
  }),
  computed: {
    startDatePickerOptions () {
      return {
        disabledDate: time => this.condition.endTime && time.getTime() > new Date(this.condition.endTime)
      }
    },
    endDatePickerOptions () {
      return {
        disabledDate: time => {
          time = time.getTime()
          return new Date() < time || (this.condition.beginTime && time < new Date(this.condition.beginTime))
        }
      }
    },
    factorMap () {
      return this.currentFactorMap.map
    },
    selectedCount () {
      const {factorMap} = this

      return this.condition.factorIds.reduce((map, factorId) => {
        const factor = factorMap[factorId]

        if (factor) {
          const {parentId} = factor

          if (map.hasOwnProperty(parentId)) {
            map[parentId] += 1
          }
        }

        return map
      }, this.formatFactorGroups.reduce((map, factorGroup) => {
        map[factorGroup.id] = 0
        return map
      }, {}))
    }
  },
  methods: {
    setDataTimeRange (condition) {
      const endTime = new Date()
      const beginTime = new Date()
      endTime.setDate(endTime.getDate() - 1)
      beginTime.setDate(beginTime.getDate() - 8)

      condition.beginTime = beginTime.format('YYYY/MM/DD 00:00:00')
      condition.endTime = endTime.format('YYYY/MM/DD 23:59:59')
    },
    setAuditType (condition) {
      const {auditTypes} = this
      condition.auditType = auditTypes[auditTypes.length - 1].value
    },
    setDataType (condition) {
      condition.dataType = this.dataTypes[0].value
    },
    setSiteId (condition) {
      const {sites} = this

      if (sites.length > 0) {
        condition.siteId = sites[0].id
      }
    },
    clearFactorIds (condition) {
      condition.factorIds = []
    },
    buildFactorGroupSelectMap () {
      const {selectedCount} = this
      this.factorGroupSelectMap = this.formatFactorGroups.reduce((map, factorGroup) => {
        const groupId = factorGroup.id
        map[groupId] = factorGroup.children.length === selectedCount[groupId]
        return map
      }, {})
    },
    setCompareJSONStr () {
      const {condition} = this
      this.conditionJSONStr = JSON.stringify(Object.keys(condition).reduce((json, key) => {
        if (!baseRunKeyMap[key]) {
          json[key] = condition[key]
        }
        return json
      }, {}))
    },
    reset () {
      const {condition} = this
      condition.id = null
      this.setDataTimeRange(condition)
      this.setAuditType(condition)
      this.setDataType(condition)
      this.setSiteId(condition)
      this.clearFactorIds(condition)
      this.buildFactorGroupSelectMap()
      this.setCompareJSONStr()

      this.$nextTick(() => {
        this.$refs.form.clearValidate()
      })
    },
    beginTimeValidator (rule, value, callback) {
      const {endTime} = this.condition
      const diff = new Date(endTime) - new Date(value)

      if (Math.ceil(diff / (24 * 60 * 60 * 1000)) < 7) {
        callback(new Error('时间范围不能小于7天'))
      }

      return callback()
    },
    endTimeValidator (rule, value, callback) {
      const {beginTime} = this.condition
      const diff = new Date(value) - new Date(beginTime)

      if (Math.ceil(diff / (24 * 60 * 60 * 1000)) < 7) {
        callback(new Error('时间范围不能小于7天'))
      }

      return callback()
    },
    onSiteItemClick (site) {
      const {condition} = this
      const siteId = site.id

      if (condition.siteId === siteId) {
        return
      }

      condition.siteId = siteId

      const {factorMap} = this
      condition.factorIds = condition.factorIds.filter(factorId => factorMap[factorId])
      this.buildFactorGroupSelectMap()

      this.$nextTick(() => {
        this.$refs.form.clearValidate()
      })
    },
    onFactorGroupCheckChange (factorGroup, checked) {
      const {condition} = this
      const {factorIds} = condition

      if (checked) {
        const factorSelectedMap = factorIds.reduce((map, factorId) => {
          map[factorId] = true
          return map
        }, {})

        factorGroup.children.forEach(factor => {
          const factorId = factor.id

          if (!factorSelectedMap[factorId]) {
            factorIds.push(factorId)
          }
        })
      } else {
        const factorGroupFactorMap = factorGroup.children.reduce((map, factor) => {
          map[factor.id] = true
          return map
        }, {})
        condition.factorIds = factorIds.filter(factorId => !factorGroupFactorMap[factorId])
      }
    },
    onFactorSelectChange () {
      this.buildFactorGroupSelectMap()
    },
    nextStep () {
      return new Promise((resolve, reject) => {
        this.$refs.form.validate(flag => {
          if (!flag) {
            return reject(new Error(''))
          }

          let {condition} = this
          condition.factorIds.sort((a, b) => a.localeCompare(b))

          const conditionJSONs = Object.keys(condition).reduce((list, key) => {
            list[baseRunKeyMap[key] | 0][key] = condition[key]
            return list
          }, [{}, {}])

          if (JSON.stringify(conditionJSONs[0]) === this.conditionJSONStr) {
            resolve()
          } else {
            this.changeLoadStatus(true)
            this.$api.PMFAnalysis.termInput(condition).then(data => {
              condition.id = data.id
              resolve(Object.freeze(data))
            }).catch(error => {
              console.error(error)
              reject(error)
            }).finally(() => {
              condition = null
              this.changeLoadStatus(false)
            })
          }
        })
      })
    }
  },
  created () {
    this.setCompareJSONStr()
    this.buildFactorGroupSelectMap()
  }
}
</script>

<style lang="scss" scoped>
.pmf-search-term-form {
  display: grid;
  grid-template-columns: max-content minmax(max-content, 300px) auto;
  grid-column-gap: 12px;
  padding-bottom: .5rem;

  > * {
    min: {
      width: 0;
      height: 0;
    }
  }
}

.base-term {
  display: flex;
  flex-direction: column;

  > .el-radio-group {
    display: flex;
    flex-wrap: nowrap;
  }
}

.select-areas {
  /*display: grid;*/
  /*grid-template-columns: minmax(max-content, 300px) auto;*/
  /*grid-column-gap: 1rem;*/

  > * {
    min: {
      width: 0;
      height: 0;
    };
    width: 100%;
    height: 100%;
  }
}

.select-area-form-item {
  /deep/ {
    .el-form-item__content {
      height: 100%;

      > *:first-child {
        height: calc(100% - 32px);
      }
    }
  }
}

.site-select-area {
  margin: 0;
  padding: 0;

  > li {
    padding: 0 1rem;

    &:hover {
      background-color: #F5F7FA;
    }

    &.selected {
      background-color: #ecf5ff;
    }
  }
}

.factor-select-area-wrapper {
  display: grid;
  grid-template-rows: max-content 1px auto;
  width: 100%;
  border: 1px solid #EBEEF5 {
    radius: 4px;
  };

  .divider {
    background-color: #EBEEF5;
  }
}

.factor-group-select-list {
  margin: 0;
  padding: 0;
  list-style: none;
  overflow: auto;

  > li {
    padding: 0 1rem;
  }
}

.factor-group {

}

.factor-select-list {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  margin: 0;
  padding: 0 1rem;
}
</style>



// WEBPACK FOOTER //
// src/views/04Statistical-Analysis/11PMF-Analysis/01PM/01Run/steps/01TermInputStep.vue